import React, { useState } from 'react';

import type { BackendTypes } from '@tf/api';
import { S } from '@tf/utils';

import type { CaseRequestStatusChanged, CaseStatusChanged } from '@/core/api/profileEvents';
import type { ObjectType } from '@/core/api/utils/typeUtils';
import { BaseEventLog } from '@/components/shared';
import type { Event } from '@/components/shared/BaseEventLog';

type PaymentAssignedManagersChanged = ObjectType<
	'payment_assigned_managers_changed',
	{
		new_managers?: string[];
		removed_managers?: string[];
	}
>;

type PaymentStatusChanged = ObjectType<
	'payment_status_changed',
	{
		new_status?: 'ORDER_COMPLETED' | 'ORDER_REJECTED' | 'FAILED' | 'PENDING_REVIEW';
	}
>;

export const PAYMENT_EVENT_MESSAGES: Record<string, (e: any) => string> = {
	payment_commented: () => 'commented on the payment',
	payment_created: () => 'created the payment',
	payment_updated: () => 'updated the payment',
	payment_assigned_managers_changed: (e: PaymentAssignedManagersChanged) => {
		if (e.meta.new_managers?.length) {
			return 'assigned a new manager to the payment';
		}
		return 'removed the manager from the payment';
	},
	payment_status_changed: (e: PaymentStatusChanged) => {
		if (!e.meta.new_status) return 'updated the payment status';
		return `updated the payment status to ${S.prettify(e.meta.new_status).toUpperCase()}`;
	},
	payment_assessed: () => 'calculated the payment risk rating',
	case_status_changed: (e: CaseStatusChanged) => {
		switch (e.meta.case_status) {
			case 'ACTIVE':
				return 'opened a new case';
			case 'CLOSED':
				return 'closed the case';
			default:
				return `changed case status to ${e.meta.case_status}`;
		}
	},

	case_request_status_changed: (e: CaseRequestStatusChanged) => {
		switch (e.meta.case_request_status) {
			case 'VIEWED':
				return 'viewed the case';
			case 'REVOKED':
				return 'revoked the case request';
			case 'UPDATED':
				return 'updated the case';
			case 'SENT':
				return 'sent the case request';
			default:
				return `changed case status to ${e.meta.case_request_status}`;
		}
	},
};

const useFilteredEvents = (events: Event[]) => {
	const [searchValue, setSearchValue] = useState('');

	const filteredEvents =
		events?.filter((event) => {
			if (!searchValue) return true;
			const eventLabel = PAYMENT_EVENT_MESSAGES[event.kind]?.(event)?.toLowerCase() ?? '';
			return eventLabel?.includes(searchValue.toLowerCase());
		}) ?? [];

	return { filteredEvents, searchValue, setSearchValue };
};

interface Props {
	events: Event[];
	listUsers: BackendTypes.UserInfo[];
}

export const PaymentsEventLog: React.FC<Props> = ({ listUsers, events }) => {
	const { setSearchValue, searchValue, filteredEvents } = useFilteredEvents(events);

	return (
		<BaseEventLog
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			events={filteredEvents}
			listUsers={listUsers}
			messagesMap={PAYMENT_EVENT_MESSAGES}
		/>
	);
};
